import { useState } from 'react'
import { USER_POI_TYPES } from '../../data/userPoiTypes'
import { UserPOIType } from '../../types/userPois'
import { Plus } from 'lucide-react'

interface UserPOIDrawerProps {
  onStartDrag: (type: UserPOIType) => void
  isAuthenticated: boolean
  isCompact?: boolean // For mobile/tablet - show only icons
}

export function UserPOIDrawer({ onStartDrag, isAuthenticated, isCompact = false }: UserPOIDrawerProps) {
  const [isDragging, setIsDragging] = useState<string | null>(null)

  const handleDragStart = (e: React.DragEvent, typeId: string) => {
    if (!isAuthenticated) {
      e.preventDefault()
      alert('Please sign in to add reports')
      return
    }
    
    setIsDragging(typeId)
    e.dataTransfer.setData('userPoiType', typeId)
    e.dataTransfer.effectAllowed = 'copy'
    
    // Create a custom drag image
    const dragImage = document.createElement('div')
    dragImage.className = 'user-poi-drag-image'
    dragImage.style.position = 'absolute'
    dragImage.style.top = '-1000px'
    dragImage.style.padding = '8px 16px'
    dragImage.style.background = '#374151'
    dragImage.style.color = 'white'
    dragImage.style.borderRadius = '8px'
    dragImage.style.fontSize = '14px'
    dragImage.style.fontWeight = '500'
    dragImage.textContent = USER_POI_TYPES.find(t => t.id === typeId)?.name || ''
    document.body.appendChild(dragImage)
    e.dataTransfer.setDragImage(dragImage, 0, 0)
    setTimeout(() => document.body.removeChild(dragImage), 0)
    
    onStartDrag(typeId as UserPOIType)
  }

  const handleDragEnd = () => {
    setIsDragging(null)
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header - Responsive sizing */}
      <div className="p-2 md:p-3 border-b border-gray-700">
        <h3 className="text-xs md:text-sm font-medium text-gray-200">Add Route Report</h3>
        <p className="hidden md:block text-xs text-gray-400 mt-1">
          Drag a marker onto the map to report hazards, conditions, or tips
        </p>
      </div>

      {/* Draggable POI Types - Responsive layout */}
      <div className="overflow-y-auto p-2 md:p-3 space-y-2 min-h-[400px]">
        {isAuthenticated ? (
          <>
            {USER_POI_TYPES.map(poiType => {
              const Icon = poiType.icon
              const isBeingDragged = isDragging === poiType.id
              
              return (
                <div
                  key={poiType.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, poiType.id)}
                  onDragEnd={handleDragEnd}
                  className={`
                    flex items-center p-2 md:p-3 rounded-lg bg-gray-700 
                    hover:bg-gray-600 transition-all cursor-move
                    ${isBeingDragged ? 'opacity-50 scale-95' : 'hover:scale-[1.02]'}
                  `}
                >
                  {/* Icon with responsive sizing */}
                  <div 
                    className="flex-shrink-0 w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: poiType.color }}
                  >
                    <Icon className="w-4 h-4 md:w-5 md:h-5 text-white" />
                  </div>
                  
                  {/* Text content - hidden on mobile only */}
                  <div className="hidden sm:flex flex-1 ml-2 sm:ml-3 min-w-0">
                    <div className="flex-1">
                      <h4 className="text-xs sm:text-sm font-medium text-white sm:truncate md:whitespace-normal md:break-words">
                        {poiType.name}
                      </h4>
                      <p className="hidden md:block text-xs text-gray-400 whitespace-normal break-words">
                        {poiType.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Plus icon - visible on all screen sizes */}
                  <div className="flex-shrink-0 ml-2">
                    <Plus className="w-4 h-4 md:w-5 md:h-5 text-gray-400" />
                  </div>
                  
                </div>
              )
            })}
            
            {/* Instructions - Now inside the scrollable area with spacing */}
            <div className="mt-8 p-2 md:p-3 border border-gray-700 bg-gray-700/30 rounded-lg">
              <p className="text-[10px] md:text-xs text-gray-400">
                <span className="hidden md:inline">💡 Tip: </span>
                <span className="md:hidden">Drag to map to add reports</span>
                <span className="hidden md:inline">Drag markers onto the map to report hazards, conditions, or tips</span>
              </p>
            </div>
            
            {/* Extra padding at the bottom for elevation panel */}
            <div className="h-96" />
          </>
        ) : (
          <div className="text-center py-8 text-gray-400">
            <p className="text-sm">Please sign in to add reports</p>
          </div>
        )}
      </div>
    </div>
  )
}